import type { SpeciesContext } from './species.js'

export type Sex = 'M' | 'F' | 'U'

/*
 * `AnimalSummary` stava qui ed è stato tolto il 9 agosto.
 *
 * Era la forma del soggetto scritta a mano, e dichiarava `photo_url` mentre
 * il server manda `photo_path`: un campo che a runtime sarebbe stato sempre
 * `undefined` senza un solo errore di compilazione. La forma delle risposte
 * ora si deriva dallo schema (`AnimalRead` in `@pedigreesmart/api-client`);
 * qui restano solo i tipi che descrivono **ragionamenti** — la genealogia e
 * la consanguineità — che nessuno schema può dare.
 */

export interface PedigreeNode {
  id: number | null
  name: string
  full_name?: string | null
  registry_number: string | null
  sex: Sex
  birth_date?: string | null
  color?: string | null
  breed?: string | null
  inbreeding_coefficient?: number | null
  /**
   * `dam` e `sire` sono assenti quando il ramo finisce, non `null`: il
   * serializzatore toglie le chiavi vuote. Vanno letti con `?.`, non
   * confrontati con `null`.
   */
  dam?: PedigreeNode | null
  sire?: PedigreeNode | null
}

/**
 * Un antenato che compare su entrambi i rami: è ciò che rende consanguineo un
 * soggetto.
 *
 * Le due profondità sono contate da ciascun genitore, quindi un accoppiamento
 * padre-figlia ha il padre a `dam_side_depth: 1` e `sire_side_depth: 0`.
 * `contribution` è quanto quell'antenato pesa sul coefficiente totale, non una
 * percentuale.
 */
export interface CommonAncestor {
  id: number
  name: string | null
  dam_side_depth: number
  sire_side_depth: number
  contribution: number
}

export interface Pedigree {
  generations: number
  root: PedigreeNode
  /**
   * Ordinati per `contribution` decrescente: il primo è quello che pesa di
   * più, ed è quello da nominare se se ne nomina uno solo.
   *
   * Il campo `paths` che questo tipo dichiarava non esiste nella risposta:
   * sarebbe arrivato `undefined` a runtime senza un errore di compilazione.
   */
  common_ancestors: readonly CommonAncestor[]
  truncated: boolean
}

/**
 * Labels the sex using the species vocabulary where it has one — some
 * species name the sexes rather than saying "male"/"female".
 */
export function sexLabel(sex: Sex, species: SpeciesContext | null): string {
  if (sex === 'M') return species?.terminology['sex.male'] ?? 'Maschio'
  if (sex === 'F') return species?.terminology['sex.female'] ?? 'Femmina'

  return 'Non determinato'
}

/**
 * Quanti antenati contiene davvero un albero: è il numero che dice a chi
 * guarda quanto è completo. Un albero a quattro generazioni ne ammette al
 * massimo trenta.
 *
 * **Il soggetto stesso non si conta**: è la radice, non un suo antenato. La
 * versione precedente lo contava, quindi il risultato era sempre uno in più
 * di quello che `maxAncestors()` dichiara come massimo — e un albero completo
 * risultava «31 su 30».
 */
export function countAncestors(root: PedigreeNode | null | undefined): number {
  if (!root) return 0

  return conta(root.dam) + conta(root.sire)
}

function conta(node: PedigreeNode | null | undefined): number {
  if (!node) return 0

  // Un nodo senza id è un antenato dichiarato ma non presente a registro:
  // occupa una casella dell'albero e non è un soggetto che abbiamo.
  return (node.id === null ? 0 : 1) + conta(node.dam) + conta(node.sire)
}

/** Maximum ancestors for a given depth: 2^(n+1) - 2, root excluded. */
export function maxAncestors(generations: number): number {
  return 2 ** (generations + 1) - 2
}

/**
 * How complete a pedigree is, 0..1. Breeders read this before a mating: a
 * sparse tree means the inbreeding coefficient is not trustworthy.
 */
export function pedigreeCompleteness(pedigree: Pedigree): number {
  const max = maxAncestors(pedigree.generations)
  if (max === 0) return 1

  // Nessuna sottrazione della radice: `countAncestors()` conta gli antenati e
  // non il soggetto. La versione precedente la toglieva a mano perché quella
  // funzione lo contava, e le due cose si compensavano — un equilibrio che si
  // rompeva alla prima correzione di una delle due.
  return Math.min(1, countAncestors(pedigree.root) / max)
}
